import { useAtom } from '@reatom/npm-react'
import * as DateF from 'date-fns'
import { Trans } from 'react-i18next'
import styled from 'styled-components'

import { Button, Typography, useHapticFeedback } from '@blade/ui-kit'
import { type CurrencyModel } from '@root/entities/currency'
import { DATE_FORMAT } from '@root/shared'
import { useTranslation } from '@root/shared/localization'

import { PremiumText } from '../constants'
import { PremiumCardHeader } from '../premium-card-header'

import type { PremiumMetaModel } from '../../atoms'
import type { CurrencyWithPremium } from '@root/entities/currency'

type Props = {
  currency: CurrencyModel
  premium: PremiumMetaModel
  onClick?: (currency: CurrencyWithPremium.CurrencyWithPremium) => void

  onBuyAgain: (currency: CurrencyWithPremium.CurrencyWithPremium) => void
}

export const ExpiredPremiumCard: React.FC<Props> = ({
  currency,
  onClick,
  premium,
  onBuyAgain,
}) => {
  const haptics = useHapticFeedback()
  const [expiresAt] = useAtom(premium.expiresAt)

  const { t } = useTranslation()

  const handleBuyAgain = () => {
    haptics.impactOccurred('light')
    onBuyAgain({
      currency,
      premium,
    })
  }

  return (
    <ExpiredPremiumCardRoot
      onClick={() => {
        if (onClick) {
          haptics.impactOccurred('light')
          onClick({ currency, premium })
        }
      }}
    >
      {/* no editing for expired premium */}
      <Header>
        <PremiumCardHeader currency={currency} premium={premium} />
      </Header>
      <Footer>
        <FooterContent>
          <DateEnded>
            <PremiumText>{t('common.d-premium')}</PremiumText>{' '}
            <Trans
              i18nKey="premium.purchased.date-ended"
              components={[<span key={0} />]}
              values={{
                date: DateF.format(expiresAt, DATE_FORMAT.dd_MM_yy),
              }}
            />
          </DateEnded>
          <ButtonBuy
            variant="solid"
            size="xs"
            radius="full"
            onClick={handleBuyAgain}
          >
            {t('premium.button.buy-again')}
          </ButtonBuy>
        </FooterContent>
      </Footer>
    </ExpiredPremiumCardRoot>
  )
}

export const ExpiredPremiumCardRoot = styled.div`
  border-radius: 8px;
  background: var(--background-2);
  display: grid;

  padding: 3px;
`

const Header = styled.div`
  padding: 10px 14px;
`

const Footer = styled.footer`
  padding: 6px 14px;
  padding-inline-end: 6px;
  display: grid;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  background: var(--appearanceA-4);
`

const FooterContent = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
`

const DateEnded = styled(Typography).attrs({
  variant: 'description-semi-bold',
  component: 'span',
})`
  color: var(--textA-9);
  text-align: left;
`

const ButtonBuy = styled(Button)`
  background: var(--backgroundA-4);
  font-size: var(--typography-description-semi-bold-font-size);
  line-height: var(--typography-description-semi-bold-line-height);
  font-weight: var(--typography-description-semi-bold-font-weight);
  color: var(--subscriptionButton-9);

  padding-inline: 15px;
  height: 30px;

  --button-radius-full: 6px;

  &:hover {
    background: var(--backgroundA-5);
  }
`
